import { useQuery, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import type { FileNode } from "../api.ts";
import { usePageDropTarget } from "../lib/drop.ts";
import { formatBytes, formatCount, formatExactBytes, formatNumber } from "../lib/format.ts";
import { nodesQuery, usageQuery } from "../lib/queries.ts";
import { MAX_UPLOAD_LABEL } from "../lib/uploads.ts";
import { Breadcrumbs } from "./Breadcrumbs.tsx";
import { FileBadge } from "./FileBadge.tsx";
import {
  FileTable,
  filterFiles,
  SORT_CHOICES,
  type Sort,
  sortChoice,
  sortFiles,
  sortOfChoice,
} from "./FileTable.tsx";
import { FolderHeader, ShareLinksDisclosure, SharingPanel } from "./FolderHeader.tsx";
import styles from "./FolderView.module.css";
import { GooglyEyes } from "./GooglyEyes.tsx";
import { Icon } from "./Icon.tsx";
import { NewFolderButton } from "./NewFolderButton.tsx";
import { PickFiles } from "./PickFiles.tsx";
import ui from "./ui.module.css";

export function FolderView({ folder }: { folder: FileNode | null }) {
  const parentId = folder?.id ?? null;
  const nodes = useSuspenseInfiniteQuery(nodesQuery(parentId));
  const items = nodes.data.pages.flatMap((page) => page.items);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<Sort | null>(null);

  usePageDropTarget({ kind: "new", parentId }, folder?.name ?? null);

  if (items.length === 0 && !folder) return <EmptyDrive />;

  const visible = sortFiles(filterFiles(items, query), sort);

  return (
    <div className={styles.page}>
      {folder ? (
        <>
          <Breadcrumbs ancestors={folder.ancestors} />
          <FolderHeader node={folder} />
          <div className={styles.sharingStack}>
            <SharingPanel node={folder} />
            <ShareLinksDisclosure node={folder} />
          </div>
        </>
      ) : null}

      {items.length === 0 && folder ? (
        <EmptyFolder parentId={folder.id} />
      ) : (
        <>
          <div className={styles.top}>
            {folder ? null : <RootIntro />}
            <Filters
              query={query}
              onQuery={setQuery}
              sort={sort}
              onSort={setSort}
              total={items.length}
              matches={visible.length}
            />
          </div>

          {visible.length === 0 ? (
            <div className={styles.noMatch}>
              <GooglyEyes />
              <p className={styles.noMatchTitle}>Nothing here matches “{query}”</p>
              <p className={styles.noMatchDetail}>Try a shorter word, or a kind like PDF.</p>
              <button type="button" className={ui.button} onClick={() => setQuery("")}>
                <Icon name="close" />
                Clear the filter
              </button>
            </div>
          ) : (
            <FileTable nodes={visible} sort={sort} onSort={setSort} />
          )}

          {nodes.hasNextPage ? (
            <div className={styles.more}>
              <button
                type="button"
                className={ui.button}
                onClick={() => void nodes.fetchNextPage()}
                disabled={nodes.isFetchingNextPage}
              >
                {nodes.isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function RootIntro() {
  return (
    <div className={styles.intro}>
      <div className={styles.heading}>
        <h1 className={styles.title}>Files</h1>
        <UsageLine />
      </div>
      <div className={styles.introActions}>
        <p className={styles.dropHint}>or drop files anywhere</p>
        <NewFolderButton parentId={null} />
        <PickFiles variant="primary" label="Upload files" className={styles.uploadButton}>
          <Icon name="upload" />
          Upload files
        </PickFiles>
      </div>
    </div>
  );
}

type FilterProps = {
  query: string;
  onQuery: (query: string) => void;
  sort: Sort | null;
  onSort: (sort: Sort | null) => void;
  total: number;
  matches: number;
};

function Filters({ query, onQuery, sort, onSort, total, matches }: FilterProps) {
  const filterId = useId();
  const sortId = useId();

  return (
    <div className={styles.filters}>
      <div className={styles.search}>
        <label htmlFor={filterId} className={ui.visuallyHidden}>
          Filter this folder
        </label>
        <Icon name="search" className={styles.searchIcon} />
        <input
          id={filterId}
          type="search"
          className={`${ui.input} ${styles.searchInput}`}
          value={query}
          placeholder="Filter this folder — mix.mp3, PDF"
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => onQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") onQuery("");
          }}
        />
        {query ? (
          <button
            type="button"
            className={styles.clear}
            onClick={() => onQuery("")}
            aria-label="Clear the filter"
            title="Clear"
          >
            <Icon name="close" />
          </button>
        ) : null}
      </div>
      <div className={styles.sortField}>
        <label htmlFor={sortId} className={styles.sortLabel}>
          Sort
        </label>
        <select
          id={sortId}
          className={styles.sortSelect}
          value={sortChoice(sort)}
          onChange={(event) => onSort(sortOfChoice(event.target.value))}
        >
          {SORT_CHOICES.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      </div>
      <p className={styles.count} aria-live="polite">
        {query ? `${formatNumber(matches)} of ${formatCount(total, "item", "items")} match` : ""}
      </p>
    </div>
  );
}

function UsageLine() {
  const usage = useQuery(usageQuery);
  if (!usage.data) {
    return <span className={`${ui.skeleton} ${styles.usageSkeleton}`} aria-hidden="true" />;
  }
  const { files, liveBytes, totalBytes } = usage.data.storage;
  const extra = totalBytes - liveBytes;
  return (
    <Link to="/usage" className={styles.usageLink}>
      <dl className={styles.usage}>
        <div>
          <dt>Live</dt>
          <dd title={formatExactBytes(liveBytes)}>{formatBytes(liveBytes)}</dd>
        </div>
        <div>
          <dt>Extra</dt>
          <dd title={`${formatExactBytes(extra)} in superseded versions, trash and backups`}>
            {formatBytes(extra)}
          </dd>
        </div>
        <div>
          <dt>Files</dt>
          <dd>{formatNumber(files)}</dd>
        </div>
      </dl>
      <span className={styles.usageMore}>
        Usage
        <Icon name="chevron" />
      </span>
    </Link>
  );
}

function EmptyFolder({ parentId }: { parentId: string }) {
  return (
    <div className={styles.folderEmpty}>
      <GooglyEyes />
      <p className={styles.folderEmptyTitle}>Nothing in here yet</p>
      <p className={styles.noMatchDetail}>
        Files you upload here inherit this folder’s people. Up to {MAX_UPLOAD_LABEL} per file.
      </p>
      <div className={styles.emptyActions}>
        <NewFolderButton parentId={parentId} />
        <PickFiles target={{ kind: "new", parentId }} variant="primary" label="Upload files">
          <Icon name="upload" />
          Upload files
        </PickFiles>
      </div>
    </div>
  );
}

function EmptyDrive() {
  return (
    <div className={styles.page}>
      <div className={styles.empty}>
        <div className={styles.emptyArt} aria-hidden="true">
          <FileBadge contentType="text/html" name="page.html" size="lg" />
          <GooglyEyes size="lg" follow />
          <FileBadge contentType="audio/mpeg" name="mix.mp3" size="lg" />
        </div>
        <h1 className={styles.emptyTitle}>
          <span className={styles.pointerOnly}>Drop files here</span>
          <span className={styles.touchOnly}>Upload your first file</span>
        </h1>
        <p className={styles.emptyDetail}>
          Each file is visible only to you until you add people. Up to {MAX_UPLOAD_LABEL} per file.
        </p>
        <div className={styles.emptyActions}>
          <NewFolderButton parentId={null} />
          <PickFiles variant="primary" label="Upload files">
            <Icon name="upload" />
            Choose files
          </PickFiles>
        </div>
      </div>
    </div>
  );
}

export function ListPending({ title }: { title?: string }) {
  return (
    <div className={styles.page} aria-busy="true">
      <div className={styles.intro}>
        <div className={styles.heading}>
          {title ? <h1 className={styles.title}>{title}</h1> : null}
          <span className={`${ui.skeleton} ${styles.usageSkeleton}`} />
        </div>
      </div>
      <div className={styles.pendingList}>
        {Array.from({ length: 5 }, (_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
          <div key={index} className={styles.pendingRow}>
            <span className={ui.skeleton} />
            <span className={ui.skeleton} />
          </div>
        ))}
      </div>
    </div>
  );
}
